/* bm_peakExplorer_previewToggle.js - Peak Explorer to 3D preview bridge */
(() => {
  'use strict';

  const BM_3D_PREVIEW_ROOT_SELECTOR = '.bm_3dPreview_root[data-bm-section-id]';
  const BM_3D_PREVIEW_API_READY_EVENT = 'bm:3dPreview:api-ready';
  const BM_3D_PREVIEW_OPEN_EVENT = 'bm:3dPreview:opened';
  const BM_3D_PREVIEW_CLOSE_EVENT = 'bm:3dPreview:closed';

  function bm_peakExplorer_attachPreviewToggle({ root, button, onStatus }) {
    if (!root || !button) return null;

    const peakSectionId = String(root.dataset.sectionId || '').trim();
    const mapElement = root.querySelector('[data-role="map"]');
    const previewRoot = bm_peakExplorer_findPreviewRoot(peakSectionId);
    if (!mapElement || !previewRoot) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.title = 'Die 3D-Vorschau ist fuer diese Auswahl nicht konfiguriert.';
      return null;
    }

    const previewSectionId = String(previewRoot.dataset.bmSectionId || '').trim();
    if (!previewSectionId) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.title = 'Die 3D-Vorschau hat keine gueltige Section-ID.';
      return null;
    }

    button.setAttribute('aria-controls', previewRoot.id);
    const label = button.querySelector('[data-role="preview-toggle-label"]');

    const originalParent = previewRoot.parentNode;
    const originalNextSibling = previewRoot.nextSibling;
    let isMountedInMap = false;

    const mountPreviewInMap = () => {
      if (!mapElement.isConnected || !previewRoot.isConnected) return false;
      if (!isMountedInMap) {
        mapElement.appendChild(previewRoot);
        isMountedInMap = true;
      }
      previewRoot.classList.add('bm_3dPreview_root--mapManaged');
      previewRoot.classList.add('bm_3dPreview_root--mapMounted');
      return true;
    };

    const restorePreviewPosition = () => {
      if (!isMountedInMap || !originalParent?.isConnected) return;
      originalParent.insertBefore(previewRoot, originalNextSibling);
      previewRoot.classList.remove('bm_3dPreview_root--mapMounted');
      isMountedInMap = false;
    };

    const setOpen = (isOpen) => {
      button.setAttribute('aria-checked', String(Boolean(isOpen)));
      if (label) {
        label.textContent = isOpen ? 'Zur Auswahlkarte' : '3D-Vorschau öffnen';
      }
    };

    const isPreviewApiReady = () =>
      typeof window.BM?.bm_3dPreview_openOverlay === 'function' &&
      typeof window.BM?.bm_3dPreview_closeOverlay === 'function';

    const syncAvailability = () => {
      const isReady = isPreviewApiReady();
      button.disabled = !isReady;
      button.setAttribute('aria-disabled', String(!isReady));
      button.title = isReady
        ? 'Zwischen Karten-Auswahl und 3D-Vorschau wechseln.'
        : '3D-Vorschau wird vorbereitet.';
      return isReady;
    };

    const onToggle = () => {
      if (!isPreviewApiReady()) {
        syncAvailability();
        onStatus?.('3D-Vorschau wird noch vorbereitet.', { isError: true });
        return;
      }

      const isOpen = button.getAttribute('aria-checked') === 'true';
      if (isOpen) {
        window.BM.bm_3dPreview_closeOverlay(previewSectionId);
        return;
      }

      if (!mountPreviewInMap()) {
        onStatus?.('Die 3D-Vorschau konnte nicht in die Kartenansicht eingebettet werden.', {
          isError: true
        });
        return;
      }

      window.BM.bm_3dPreview_openOverlay(previewSectionId, true);
      onStatus?.('3D-Vorschau wird geladen.', { persistent: true });
    };

    const onPreviewOpened = (event) => {
      if (String(event.detail?.sectionId || '') !== previewSectionId) return;
      mountPreviewInMap();
      setOpen(true);
    };

    const onPreviewClosed = (event) => {
      if (String(event.detail?.sectionId || '') !== previewSectionId) return;
      setOpen(false);
      restorePreviewPosition();
    };

    const onPreviewApiReady = () => {
      syncAvailability();
    };

    previewRoot.classList.add('bm_3dPreview_root--mapManaged');
    if (previewRoot.classList.contains('bm_3dPreview_root--overlayOpen')) {
      mountPreviewInMap();
      setOpen(true);
    } else {
      setOpen(false);
    }
    syncAvailability();
    button.addEventListener('click', onToggle);
    document.addEventListener(BM_3D_PREVIEW_API_READY_EVENT, onPreviewApiReady);
    document.addEventListener(BM_3D_PREVIEW_OPEN_EVENT, onPreviewOpened);
    document.addEventListener(BM_3D_PREVIEW_CLOSE_EVENT, onPreviewClosed);

    return () => {
      button.removeEventListener('click', onToggle);
      document.removeEventListener(BM_3D_PREVIEW_API_READY_EVENT, onPreviewApiReady);
      document.removeEventListener(BM_3D_PREVIEW_OPEN_EVENT, onPreviewOpened);
      document.removeEventListener(BM_3D_PREVIEW_CLOSE_EVENT, onPreviewClosed);
      if (previewRoot.classList.contains('bm_3dPreview_root--overlayOpen')) {
        window.BM?.bm_3dPreview_closeOverlay?.(previewSectionId);
      }
      restorePreviewPosition();
      previewRoot.classList.remove('bm_3dPreview_root--mapManaged');
      previewRoot.classList.remove('bm_3dPreview_root--mapMounted');
    };
  }

  function bm_peakExplorer_findPreviewRoot(peakSectionId) {
    const previewRoots = Array.from(document.querySelectorAll(BM_3D_PREVIEW_ROOT_SELECTOR));
    const explicitlyLinked = previewRoots.filter(
      (previewRoot) => String(previewRoot.dataset.bmSelectionSectionId || '').trim() === peakSectionId
    );
    if (explicitlyLinked.length === 1) return explicitlyLinked[0];
    if (explicitlyLinked.length > 1) return null;

    const unlinked = previewRoots.filter(
      (previewRoot) => !String(previewRoot.dataset.bmSelectionSectionId || '').trim()
    );
    return unlinked.length === 1 ? unlinked[0] : null;
  }

  window.BM = window.BM || {};
  window.BM.bm_peakExplorer_attachPreviewToggle = bm_peakExplorer_attachPreviewToggle;
})();
